import { Button, Loader, Modal, SegmentedControl, Switch } from '@mantine/core';
import { minutesToHm } from '@reditelji/shared';
import { useState } from 'react';
import { useDayMail, useDayMailMutations, type DayMailDay, type MailBatch } from '../api/mail';
import { useSession } from '../api/session';
import { BatchReport } from './Recipients';

const dayName = (d: DayMailDay) => {
  const [, m, day] = d.date.split('-');
  return `${d.label} ${Number(day)}. ${Number(m)}.`;
};

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('sl-SI', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Ljubljana' });

/**
 * The dnevno sporočilo's own controls (SPEC §1.1H): the 03:00 switch, who a
 * day's mail reaches and who it passes over, its runs, and "Pošlji zdaj".
 */
export function DayMailCard() {
  const { can } = useSession();
  const [dayId, setDayId] = useState<string | null>(null);
  const state = useDayMail(dayId);
  const { setAuto, sendNow } = useDayMailMutations();
  const [confirming, setConfirming] = useState(false);
  const [report, setReport] = useState<MailBatch | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const canWrite = can('comms:write');

  if (!state.data) {
    return (
      <div className="ml-daymail">
        <Loader size="xs" />
      </div>
    );
  }
  const { auto, signedBy, days, day, plan, runs } = state.data;
  const n = plan.recipients.length;

  return (
    <div className="ml-daymail">
      <div className="ml-daymail-row">
        <Switch
          id="day-mail-auto"
          checked={auto}
          disabled={!canWrite || setAuto.isPending}
          onChange={(e) => setAuto.mutate(e.currentTarget.checked)}
          label="Samodejno ob 03:00 na vsak dan dogodka"
        />
        <span className="ml-hint">
          {auto ? `Podpisuje ${signedBy}.` : 'Izklopljeno — nič ne gre samo od sebe.'}
        </span>
      </div>
      <div className="ml-hint">
        Prejmejo prostovoljci z izmeno tega dne, ki so jo potrdili (tudi potrditev, ki jo je vpisal
        koordinator). Povezava v sporočilu je dodatna — napotnica in shranjena povezava delujeta naprej.
      </div>

      <div className="ml-daymail-row">
        <SegmentedControl
          size="xs"
          value={day.id}
          onChange={setDayId}
          data={days.map((d) => ({ value: d.id, label: dayName(d) }))}
        />
      </div>

      <div className="ml-daymail-plan">
        <strong>
          {dayName(day)}
          {plan.afterMin > 0 ? ` od ${minutesToHm(plan.afterMin)}` : ''}: prejme {n}
        </strong>
        {plan.skipped.length > 0 ? (
          <button type="button" className="ml-daymail-link" onClick={() => setShowSkipped((v) => !v)}>
            izpuščeno {plan.skipped.length}
          </button>
        ) : null}
        {n > 0 ? (
          <div className="ml-hint">{plan.recipients.map((r) => r.name).join(', ')}</div>
        ) : null}
        {showSkipped ? (
          <ul className="ml-bulk-skipped">
            {plan.skipped.map((s) => (
              <li key={s.id}>
                {s.name} — {s.reason}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {canWrite ? (
        <div className="ml-daymail-row">
          <Button
            size="xs"
            variant="default"
            disabled={!day.mayStart || n === 0 || runs.some((r) => r.batch !== null && r.batch.status !== 'done')}
            onClick={() => setConfirming(true)}
          >
            Pošlji zdaj…
          </Button>
          {!day.mayStart ? <span className="ml-hint">Ročno le za današnji dan.</span> : null}
        </div>
      ) : null}

      {runs.length > 0 ? (
        <div className="ml-daymail-runs">
          {runs.map((run) => (
            <div key={run.id} className="ml-hint">
              {time(run.createdAt)} · {run.trigger === 'auto' ? 'samodejno' : `ročno (${run.createdBy ?? 'koordinator'})`} ·{' '}
              {run.batch ? (
                <button type="button" className="ml-daymail-link" onClick={() => setReport(run.batch)}>
                  {run.batch.status === 'done'
                    ? `poslano ${run.batch.counts.sent}${run.batch.counts.failed ? `, napak ${run.batch.counts.failed}` : ''}${run.batch.counts.skipped ? `, izpuščeno ${run.batch.counts.skipped}` : ''}`
                    : `pošilja ${run.batch.counts.sent}/${run.batch.counts.total}`}
                </button>
              ) : (
                'nikogar ni bilo za poslati'
              )}
            </div>
          ))}
        </div>
      ) : null}

      <Modal opened={confirming} onClose={() => setConfirming(false)} title="Pošlji dnevno sporočilo" centered>
        <p>
          Dnevno sporočilo za {dayName(day)} gre zdaj {n} {n === 1 ? 'osebi' : 'osebam'}. Kdor ga je ta dan že prejel,
          ga ne dobi znova.
        </p>
        <div className="ml-dialog-actions">
          <div style={{ flex: 1 }} />
          <Button size="sm" variant="default" onClick={() => setConfirming(false)}>
            Prekliči
          </Button>
          <Button
            size="sm"
            loading={sendNow.isPending}
            onClick={() => sendNow.mutate(day.id, { onSettled: () => setConfirming(false) })}
          >
            Pošlji {n}
          </Button>
        </div>
      </Modal>

      <Modal opened={report !== null} onClose={() => setReport(null)} title="Dnevno sporočilo" centered>
        {report ? <BatchReport batch={report} onClose={() => setReport(null)} /> : null}
      </Modal>
    </div>
  );
}
